import axios, { AxiosResponse } from "axios";
import Cookies from 'js-cookie';

const apiUrl = process.env.REACT_APP_API_URL;
const timeoutMs = 10000;

/**
 * Obtiene los encabezados HTTP necesarios para las solicitudes,
 * incluyendo el token de autorización almacenado en cookies.
 * 
 * @returns Un objeto con los headers para peticiones JSON y autorización.
 */
const getHeaders = () => {
    const token = Cookies.get('token');

    return {
        "Content-Type": "application/json",
        "Authorization": `Token ${token}`
    };
}

/**
 * Obtiene los parámetros necesarios para consultas,
 * extrayendo y convirtiendo los valores almacenados en cookies.
 * 
 * @returns Un objeto con los parámetros `Nempresa` y `Nembarcacion`,
 *          convertidos a números o cadenas vacías si no existen.
 */
const getParams = () => {
    const nempresa = Cookies.get('Nempresa');
    const nembarcacion = Cookies.get('Nembarcacion');

    return {
        "Nempresa": nempresa !== "" ? Number(nempresa) : "",
        "Nembarcacion": nembarcacion !== "" ? Number(nembarcacion) : ""
    };
}





// Alertype
/**
 * Obtiene los tipos de alerta (`alertype`) desde el almacenamiento local
 * o realiza una llamada HTTP si no hay datos en caché o son inválidos.
 * 
 * - Intenta obtener datos cacheados de localStorage y validarlos.
 * - Si los datos cacheados son válidos, los devuelve directamente.
 * - Si no, realiza una petición GET a la API para obtener los datos.
 * - Guarda la respuesta en localStorage para futuras consultas.
 * 
 * @returns Una promesa que resuelve con la respuesta Axios con los datos de tipos de alerta.
 */
export const getAlertype = async (): Promise<AxiosResponse> => {
    const cachedData = localStorage.getItem("alertype");

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);

            if (parsed?.data?.data && parsed.data.data.length > 0) {
                return parsed;
            }
        } catch (error) {
            console.warn("Error parseando localStorage vessel:", error);
        }
    }

    const response = await axios.get(
        `${apiUrl}/alertype/`,
        {
            headers: getHeaders(),
        }
    );

    localStorage.setItem("alertype", JSON.stringify(response));

    return response;
};

// Company
/**
 * Obtiene la lista de empresas (`company`) desde el almacenamiento local
 * o realiza una llamada HTTP si no hay datos en caché o son inválidos.
 * 
 * - Intenta obtener datos cacheados de localStorage y validarlos.
 * - Si los datos cacheados son válidos, los devuelve directamente.
 * - Si no, realiza una petición GET a la API para obtener los datos,
 *   enviando los parámetros correspondientes.
 * - Guarda la respuesta en localStorage para futuras consultas.
 * 
 * @returns Una promesa que resuelve con la respuesta Axios con los datos de empresas.
 */
export const getCompany = async (): Promise<AxiosResponse> => {
    const cachedData = localStorage.getItem("company");

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);

            if (parsed?.data?.data && parsed.data.data.length > 0) {
                return parsed;
            }
        } catch (error) {
            console.warn("Error parseando localStorage vessel:", error);
        }
    }

    const response = await axios.get(
        `${apiUrl}/company/`,
        {
            headers: getHeaders(),
            params: getParams(),
        }
    );

    localStorage.setItem("company", JSON.stringify(response));

    return response;
};

/**
 * Envía una solicitud POST para crear una nueva empresa con los datos proporcionados.
 * 
 * - Realiza la petición POST a la API con el cuerpo `body`.
 * - Actualiza el caché de empresas en localStorage llamando a `getCompany`.
 * - Retorna la respuesta del servidor.
 * 
 * @param body Datos de la nueva empresa a crear.
 * @returns Una promesa que resuelve con la respuesta Axios de la creación.
 */
export const postCompany = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/company/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    const dataResponse = getCompany();

    localStorage.setItem("company", JSON.stringify(dataResponse));

    return response;
};

/**
 * Actualiza parcialmente una empresa con los datos proporcionados.
 * 
 * - Realiza la petición PATCH a la API con el cuerpo `body`.
 * - Actualiza el caché de empresas en localStorage llamando a `getCompany`.
 * - Retorna la respuesta del servidor.
 * 
 * @param body Datos parciales para actualizar la empresa.
 * @returns Una promesa que resuelve con la respuesta Axios de la actualización.
 */
export const patchCompany = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.patch(
        `${apiUrl}/company/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    const dataResponse = getCompany();

    localStorage.setItem("company", JSON.stringify(dataResponse));

    return response;
};

/**
 * Elimina una empresa enviando los datos correspondientes.
 * 
 * - Realiza la petición DELETE a la API con los datos `data`.
 * - Actualiza el caché de empresas en localStorage llamando a `getCompany`.
 * - Retorna la respuesta del servidor.
 * 
 * @param data Datos necesarios para eliminar la empresa.
 * @returns Una promesa que resuelve con la respuesta Axios de la eliminación.
 */
export const deleteCompany = async (data: any): Promise<AxiosResponse> => {
    const response = await axios.delete(
        `${apiUrl}/company/`,
        {
            headers: getHeaders(),
            data: data,
        }
    );

    const dataResponse = getCompany();

    localStorage.setItem("company", JSON.stringify(dataResponse));

    return response;
};

// Connection
/**
 * Obtiene la lista de conexiones desde la API.
 * 
 * - Realiza una petición GET a la ruta `/connection/`.
 * - Incluye headers y parámetros obtenidos de cookies para autenticación y filtrado.
 * - Retorna la respuesta Axios con los datos de conexiones.
 * 
 * @returns Una promesa que resuelve con la respuesta Axios de las conexiones.
 */
export const getConnection = async (): Promise<AxiosResponse> => {
    const response = await axios.get(
        `${apiUrl}/connection/`,
        {
            headers: getHeaders(),
            params: getParams(),
        }
    );

    return response;
};

/**
 * Crea una nueva conexión enviando los datos al servidor.
 * 
 * - Realiza una petición POST a la ruta `/connection/` con el cuerpo `body`.
 * - Incluye headers para autenticación.
 * - Retorna la respuesta Axios del servidor.
 * 
 * @param body Objeto con los datos de la conexión a crear.
 * @returns Una promesa que resuelve con la respuesta Axios.
 */
export const postConnection = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/connection/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Actualiza una conexión existente enviando los datos modificados al servidor.
 * 
 * - Realiza una petición PATCH a la ruta `/connection/` con el cuerpo `body`.
 * - Incluye headers para autenticación.
 * - Retorna la respuesta Axios del servidor.
 * 
 * @param body Objeto con los datos de la conexión a actualizar.
 * @returns Una promesa que resuelve con la respuesta Axios.
 */
export const patchConnection = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.patch(
        `${apiUrl}/connection/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

// Constant
/**
 * Obtiene la lista de constantes desde la API o cache local.
 * 
 * - Intenta obtener los datos cacheados desde localStorage bajo la clave "constant".
 * - Si hay datos cacheados válidos, los retorna directamente.
 * - Si no, realiza una petición GET a `/constant/` con headers de autenticación.
 * - Guarda la respuesta obtenida en localStorage para futuros usos.
 * 
 * @returns Una promesa que resuelve con la respuesta Axios que contiene las constantes.
 */
export const getConstant = async (): Promise<AxiosResponse> => {

    const cachedData = localStorage.getItem("constant");

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);

            if (parsed?.data?.data && parsed.data.data.length > 0) {
                return parsed;
            }
        } catch (error) {
            console.warn("Error parseando localStorage vessel:", error);
        }
    }

    const response = await axios.get(
        `${apiUrl}/constant/`,
        {
            headers: getHeaders(),
        }
    );

    localStorage.setItem("constant", JSON.stringify(response));

    return response;
};

// Delete-User
/**
 * Elimina un usuario enviando los datos necesarios al endpoint correspondiente.
 * 
 * @param data Objeto con la información requerida para identificar al usuario a eliminar.
 * @returns Una promesa que resuelve con la respuesta Axios de la operación de eliminación.
 */
export const deleteUser = async (data: any): Promise<AxiosResponse> => {
    const response = await axios.delete(
        `${apiUrl}/delete-user/`,
        {
            headers: getHeaders(),
            data: data,
        }
    );

    return response;
};

// Emissions
/**
 * Envía datos de emisiones al servidor para su registro.
 * 
 * @param body Objeto con la información de emisiones a enviar.
 * @returns Una promesa que resuelve con la respuesta Axios del servidor.
 */
export const postEmissions = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/emissions/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

// Energy
/**
 * Envía datos de energía al servidor para su registro.
 * 
 * @param body Objeto con la información de energía a enviar.
 * @returns Una promesa que resuelve con la respuesta Axios del servidor.
 */
export const postEnergy = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/energy/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

// GridPoints
/**
 * Obtiene los puntos de cuadrícula (grid points) desde la API o caché local.
 * 
 * @returns Una promesa que resuelve con la respuesta Axios que contiene los puntos de cuadrícula.
 */
export const getGridPoints = async (): Promise<AxiosResponse> => {
    const cachedData = localStorage.getItem("gridPoints");

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);

            if (parsed?.data?.data && parsed.data.data.length > 0) {
                return parsed;
            }
        } catch (error) {
            console.warn("Error parseando localStorage vessel:", error);
        }
    }

    const response = await axios.get(
        `${apiUrl}/grid/`,
        {
            headers: getHeaders(),
            params: getParams(),
            timeout: timeoutMs,
        }
    );

    localStorage.setItem("gridPoints", JSON.stringify(response));

    return response;
};

/**
 * Obtiene el estado de los puntos de la cuadrícula desde la API.
 * Guarda localmente los puntos obtenidos de `getGridPoints()` en el localStorage.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta de la llamada HTTP a la API.
 */
export const getGridPointsStatus = async (): Promise<AxiosResponse> => {

    const response = await axios.get(
        `${apiUrl}/grid/`,
        {
            headers: getHeaders(),
            params: getParams(),
            timeout: timeoutMs,
        }
    );

    const dataResponse = getGridPoints();

    localStorage.setItem("gridPoints", JSON.stringify(dataResponse));

    return response;
};

/**
 * Obtiene todos los puntos de la cuadrícula, intentando primero usar datos cacheados en localStorage.
 * Si no hay datos válidos en cache, hace una llamada a la API para obtenerlos y luego los guarda en localStorage.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta de la llamada HTTP a la API o datos cacheados.
 */
export const getGridPointsAll = async (): Promise<AxiosResponse> => {
    const cachedData = localStorage.getItem("gridPointsAll");

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);

            if (parsed?.data?.data && parsed.data.data.length > 0) {
                return parsed;
            }
        } catch (error) {
            console.warn("Error parseando localStorage vessel:", error);
        }
    }

    const response = await axios.get(
        `${apiUrl}/grid/`,
        {
            headers: getHeaders(),
            params: {
                "Nempresa": ''
            },
            timeout: timeoutMs,
        }
    );

    localStorage.setItem("gridPointsAll", JSON.stringify(response));

    return response;
};

/**
 * Actualiza puntos de la cuadrícula enviando datos con una petición PATCH a la API.
 * Luego actualiza en localStorage los datos cacheados de todos los puntos y los puntos individuales.
 * 
 * @param body - Datos que se envían para actualizar la cuadrícula.
 * @returns {Promise<AxiosResponse>} Respuesta de la petición PATCH a la API.
 */
export const patchGridPoints = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.patch(
        `${apiUrl}/grid/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    const dataResponse = getGridPointsAll();

    localStorage.setItem("gridPointsAll", JSON.stringify(dataResponse));

    const dataResponse2 = getGridPoints();

    localStorage.setItem("gridPoints", JSON.stringify(dataResponse2));

    return response;
};

/**
 * Reemplaza puntos de la cuadrícula mediante una petición PUT a la API.
 * Luego actualiza los datos cacheados en localStorage tanto para todos los puntos como los individuales.
 * 
 * @param body - Datos que se envían para reemplazar la cuadrícula.
 * @returns {Promise<AxiosResponse>} Respuesta de la petición PUT a la API.
 */
export const putGridPoints = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.put(
        `${apiUrl}/grid/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    const dataResponse = getGridPointsAll();

    localStorage.setItem("gridPointsAll", JSON.stringify(dataResponse));

    const dataResponse2 = getGridPoints();

    localStorage.setItem("gridPoints", JSON.stringify(dataResponse2));

    return response;
};

// Home
/**
 * Obtiene los datos de incidentes para la vista principal (home) desde la API.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta de la petición GET a la API.
 */
export const getHomeIncident = async (): Promise<AxiosResponse> => {
    const response = await axios.get(
        `${apiUrl}/home-incident/`,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Obtiene los datos de energía para la vista principal (home) desde la API.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta de la petición GET a la API.
 */
export const getHomeEnergy = async (): Promise<AxiosResponse> => {
    const response = await axios.get(
        `${apiUrl}/home-energy/`,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Obtiene los datos de puntos de la cuadrícula para la vista principal (home) desde la API.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta de la petición GET a la API.
 */
export const getHomeGridPoint = async (): Promise<AxiosResponse> => {
    const response = await axios.get(
        `${apiUrl}/home-gridpoint/`,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

// Incident
/**
 * Obtiene la lista de incidentes desde la API.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta de la petición GET a la API.
 */
export const getIncident = async (): Promise<AxiosResponse> => {
    const response = await axios.get(
        `${apiUrl}/incident/`,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Envía un nuevo incidente a la API mediante una petición POST.
 * 
 * @param body - Datos del incidente a registrar.
 * @returns {Promise<AxiosResponse>} Respuesta de la petición POST a la API.
 */
export const postIncident = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/incident/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Actualiza un incidente existente en la API mediante una petición PATCH.
 * 
 * @param body - Datos del incidente a modificar.
 * @returns {Promise<AxiosResponse>} Respuesta de la petición PATCH a la API.
 */
export const patchIncident = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.patch(
        `${apiUrl}/incident/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

// Login
/**
 * Envía las credenciales de inicio de sesión a la API mediante una petición POST.
 * 
 * @param email - Correo electrónico del usuario.
 * @param password - Contraseña del usuario.
 * @returns {Promise<AxiosResponse>} Respuesta de la API con los datos de autenticación.
 */
export const postLogin = async (email: any, password: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/login/`,
        {
            email: email,
            password: password
        }
    );

    return response;
};

// Profile
/**
 * Obtiene la información del perfil de usuario autenticado desde la API.
 * 
 * @param token - Token de autenticación del usuario.
 * @returns {Promise<AxiosResponse>} Respuesta de la API con los datos del perfil.
 */
export const getProfile = async (token: any): Promise<AxiosResponse> => {
    const response = await axios.get(
        `${apiUrl}/profile/`,
        {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Token ${token}`
            },
        }
    );

    return response;
};

// Register
/**
 * Registra un nuevo usuario enviando sus datos a la API mediante una petición POST.
 * 
 * @param body - Datos del usuario a registrar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras el registro.
 */
export const postRegister = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/register/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

// Report
/**
 * Obtiene los datos del reporte general desde la API.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta de la petición GET a la API.
 */
export const getReport = async (): Promise<AxiosResponse> => {
    const response = await axios.get(
        `${apiUrl}/report/`,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Envía un nuevo reporte a la API mediante una petición POST.
 * 
 * @param body - Datos del reporte a registrar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras el envío.
 */
export const postReport = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/report/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Actualiza un reporte existente en la API mediante una petición PATCH.
 * 
 * @param body - Datos del reporte a actualizar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras la actualización.
 */
export const patchReport = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.patch(
        `${apiUrl}/report/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

// Reservation
/**
 * Obtiene la lista de reservas desde la API.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta de la petición GET a la API.
 */
export const getReservation = async (): Promise<AxiosResponse> => {
    const response = await axios.get(
        `${apiUrl}/reserva/`,
        {
            headers: getHeaders(),
            params: getParams(),
        }
    );

    return response;
};

/**
 * Crea una nueva reserva enviando los datos a la API mediante una petición POST.
 * 
 * @param body - Datos de la reserva a registrar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras el registro.
 */
export const postReservation = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/reserva/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Actualiza una reserva existente en la API mediante una petición PATCH.
 * 
 * @param body - Datos de la reserva a actualizar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras la actualización.
 */
export const patchReservation = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.patch(
        `${apiUrl}/reserva/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

/**
 * Elimina una reserva existente enviando sus datos a la API mediante una petición DELETE.
 * 
 * @param data - Datos de la reserva a eliminar (por lo general, incluye un ID).
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras la eliminación.
 */
export const deleteReservation = async (data: any): Promise<AxiosResponse> => {
    const response = await axios.delete(
        `${apiUrl}/reserva/`,
        {
            headers: getHeaders(),
            data: data,
        }
    );

    return response;
};

// Status
/**
 * Obtiene la lista de estados desde la API, usando caché local si está disponible y es válida.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta con los datos de estado, ya sea del caché o de la API.
 */
export const getStatus = async (): Promise<AxiosResponse> => {
    const cachedData = localStorage.getItem("status");

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);

            if (parsed?.data?.data && parsed.data.data.length > 0) {
                return parsed;
            }
        } catch (error) {
            console.warn("Error parseando localStorage vessel:", error);
        }
    }

    const response = await axios.get(
        `${apiUrl}/status/`,
        {
            headers: getHeaders(),
        }
    );

    localStorage.setItem("status", JSON.stringify(response));

    return response;
};

// Update-User
/**
 * Actualiza los datos de un usuario mediante una petición PATCH a la API.
 * 
 * @param body - Datos del usuario a actualizar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras la actualización.
 */
export const patchUpdateUser = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.patch(
        `${apiUrl}/update-user/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    return response;
};

// Vessel
/**
 * Obtiene la lista de embarcaciones desde la API, usando datos en caché si están disponibles y son válidos.
 * 
 * @returns {Promise<AxiosResponse>} Respuesta con los datos de embarcaciones, ya sea desde localStorage o desde la API.
 */
export const getVessel = async (): Promise<AxiosResponse> => {
    const cachedData = localStorage.getItem("vessel");

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);

            if (parsed?.data?.data && parsed.data.data.length > 0) {
                return parsed;
            }
        } catch (error) {
            console.warn("Error parseando localStorage vessel:", error);
        }
    }

    const response = await axios.get(
        `${apiUrl}/vessel/`,
        {
            headers: getHeaders(),
            params: getParams(),
        }
    );

    localStorage.setItem("vessel", JSON.stringify(response));

    return response;
};

/**
 * Registra una nueva embarcación en la API mediante una petición POST
 * y actualiza el caché local con los datos actualizados.
 * 
 * @param body - Datos de la embarcación a registrar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras el registro.
 */
export const postVessel = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.post(
        `${apiUrl}/vessel/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    const dataResponse = getVessel();
    console.log(dataResponse)

    localStorage.setItem("vessel", JSON.stringify(dataResponse));

    return response;
};

/**
 * Actualiza una embarcación existente mediante una petición PATCH
 * y actualiza el caché local con los datos actualizados.
 * 
 * @param body - Datos de la embarcación a modificar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras la actualización.
 */
export const patchVessel = async (body: any): Promise<AxiosResponse> => {
    const response = await axios.patch(
        `${apiUrl}/vessel/`,
        body,
        {
            headers: getHeaders(),
        }
    );

    const dataResponse = getVessel();

    localStorage.setItem("vessel", JSON.stringify(dataResponse));

    return response;
};

/**
 * Elimina una embarcación mediante una petición DELETE
 * y actualiza el caché local con los datos restantes.
 * 
 * @param data - Información necesaria para identificar la embarcación a eliminar.
 * @returns {Promise<AxiosResponse>} Respuesta de la API tras la eliminación.
 */
export const deleteVessel = async (data: any): Promise<AxiosResponse> => {
    const response = await axios.delete(
        `${apiUrl}/vessel/`,
        {
            headers: getHeaders(),
            data: data,
        }
    );

    const dataResponse = getVessel();

    localStorage.setItem("vessel", JSON.stringify(dataResponse));

    return response;
};